import { useRef, useEffect } from 'react';
import { CasePatterns, cancelHaptics, H } from './haptics';

/* ── geometry (computed once at module level) ─────────────────────────── */
const SHEETS = {
  common:    '/case_opening/sheets/common.webp',
  epic:      '/case_opening/sheets/epic.webp',
  legendary: '/case_opening/sheets/legendary.webp',
  chroma:    '/case_opening/sheets/chroma.webp',
};
const ANCHOR = { lock: [0.4526, 0.63], item: [0.5, 0.5277] };
const COLS = 6, ROWS = 5, FRAMES = 30;
const RAR = {
  common:    { c: '#6f8dff', c2: '#c3d0ff', tries: 2 },
  epic:      { c: '#a63cff', c2: '#e2b6ff', tries: 3 },
  legendary: { c: '#ff9d1c', c2: '#ffdda6', tries: 4 },
  chroma:    { c: '#35e6bf', c2: '#c2fff2', tries: 5 },
};
const TIMINGS = {
  common:    { flyIn: 440, morph: 500, insert: 280, pin: 210, yieldPause: 240, sprite: 1040 },
  epic:      { flyIn: 480, morph: 540, insert: 300, pin: 230, yieldPause: 280, sprite: 1160 },
  legendary: { flyIn: 500, morph: 580, insert: 320, pin: 240, yieldPause: 340, sprite: 1300 },
  chroma:    { flyIn: 540, morph: 620, insert: 360, pin: 250, yieldPause: 400, sprite: 1440 },
};

const SEG = [
  { l: 120,          f: u => [80, 90 + 120 * u] },
  { l: Math.PI * 20, f: u => [100 - 20 * Math.cos(u * Math.PI), 210 + 20 * Math.sin(u * Math.PI)] },
  { l: 150,          f: u => [120, 210 - 150 * u] },
  { l: Math.PI * 30, f: u => [90 + 30 * Math.cos(u * Math.PI), 60 - 30 * Math.sin(u * Math.PI)] },
  { l: 170,          f: u => [60, 60 + 170 * u] },
  { l: Math.PI * 40, f: u => [100 - 40 * Math.cos(u * Math.PI), 230 + 40 * Math.sin(u * Math.PI)] },
  { l: 130,          f: u => [140, 230 - 130 * u] },
];
const TOTAL = SEG.reduce((a, b) => a + b.l, 0);
const HANDLE = SEG.slice(0, 5).reduce((a, b) => a + b.l, 0);
const TAIL = TOTAL - HANDLE, HOOK = 24;

function clipAt(s) {
  for (const g of SEG) { if (s <= g.l) return g.f(s / g.l); s -= g.l; }
  return SEG.at(-1).f(1);
}
function pickAt(s) {
  if (s <= HANDLE) return clipAt(s);
  const d = s - HANDLE, straight = TAIL - HOOK;
  if (d <= straight) return [60, 230 + d];
  const e = d - straight;
  return [60 + e * 0.8, 230 + straight + e * 0.55];
}
const toView = ([x, y]) => [y, 200 - x];
const N = 160;
const S_CLIP = [], S_PICK = [];
for (let i = 0; i < N; i++) {
  const s = TOTAL * i / (N - 1);
  S_CLIP.push(toView(clipAt(s)));
  S_PICK.push(toView(pickAt(s)));
}
const VB = { x: 15, y: 45, w: 490, h: 110 };
const TIP = [(S_PICK.at(-1)[0] - VB.x) / VB.w, (S_PICK.at(-1)[1] - VB.y) / VB.h];
const BRK_I = Math.round((TOTAL - HOOK - 62) / TOTAL * (N - 1));
const BRK = S_PICK[BRK_I];
const BRK_F = [(BRK[0] - VB.x) / VB.w, (BRK[1] - VB.y) / VB.h];

const polyD = pts => pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');

function pieceSVG(pts, capAt, isGolden) {
  const c = capAt === 'start' ? pts[0] : pts.at(-1);
  const cut = capAt === 'start' ? pts.at(-1) : pts[0];
  const hl = capAt === 'start' ? pts.slice(0, -2) : pts.slice(2);
  const d = polyD(pts);
  const dark = isGolden ? '#7a5200' : '#5f666e';
  const stops = isGolden
    ? `<stop offset="0" stop-color="#fff8c0"/><stop offset="0.2" stop-color="#ffd966"/>
       <stop offset="0.4" stop-color="#c8860a"/><stop offset="0.55" stop-color="#ffe880"/>
       <stop offset="0.75" stop-color="#d4a017"/><stop offset="1" stop-color="#8b6000"/>`
    : `<stop offset="0" stop-color="#ffffff"/><stop offset="0.2" stop-color="#d9dde2"/>
       <stop offset="0.4" stop-color="#8a9199"/><stop offset="0.55" stop-color="#f4f6f8"/>
       <stop offset="0.75" stop-color="#a3aab2"/><stop offset="1" stop-color="#6b727a"/>`;
  return `<svg viewBox="${VB.x} ${VB.y} ${VB.w} ${VB.h}" preserveAspectRatio="xMidYMid meet">
   <defs><linearGradient id="clipMetal2" x1="30" y1="50" x2="290" y2="150" gradientUnits="userSpaceOnUse" spreadMethod="reflect">
     ${stops}
   </linearGradient></defs>
   <g fill="none" stroke-linejoin="round">
    <path d="${d}" stroke="${dark}" stroke-width="20" stroke-linecap="butt"/>
    <circle cx="${c[0]}" cy="${c[1]}" r="10" fill="${dark}"/>
    <path d="${d}" stroke="url(#clipMetal2)" stroke-width="14" stroke-linecap="butt"/>
    <circle cx="${c[0]}" cy="${c[1]}" r="7" fill="url(#clipMetal2)"/>
    <path d="M${cut[0]} ${cut[1] - 10} V${cut[1] + 10}" stroke="${dark}" stroke-width="3"/>
    <path d="${polyD(hl)}" stroke="#fff" stroke-width="3" opacity=".7" stroke-linecap="butt" transform="translate(-2 -2.5)"/>
   </g></svg>`;
}

const imgs = {};
function preload(r) {
  if (imgs[r]) return imgs[r];
  const im = new Image(); im.src = SHEETS[r];
  imgs[r] = im.decode().catch(() => {});
  return imgs[r];
}
Object.keys(SHEETS).forEach(preload);

const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── component ───────────────────────────────────────────────────────── */
export default function CaseOpeningAnim({ rarity = 'common', golden = false, broken, itemAsset, itemType, onDone }) {
  const stageRef  = useRef(null);
  const caseRef   = useRef(null);
  const fxRef     = useRef(null);
  const pickRef   = useRef(null);
  const pOutRef   = useRef(null);
  const pMidRef   = useRef(null);
  const pHiRef    = useRef(null);
  const itemRef   = useRef(null);
  const flashRef  = useRef(null);
  const floorRef  = useRef(null);
  const pwRef     = useRef(null);
  const paRef     = useRef(null);
  const pbRef     = useRef(null);
  const burstRef  = useRef(null);
  const seamRef   = useRef(null);
  const sweepRef  = useRef(null);
  const pendingOutcomeRef = useRef(null);
  const brokenRef    = useRef(broken);
  const itemAssetRef = useRef(itemAsset);
  const itemTypeRef  = useRef(itemType);
  const goldenRef    = useRef(golden);

  useEffect(() => { itemAssetRef.current = itemAsset; }, [itemAsset]);
  useEffect(() => { itemTypeRef.current  = itemType;  }, [itemType]);

  /* Resolve pending outcome when broken prop arrives — pass value to avoid stale closure */
  useEffect(() => {
    brokenRef.current = broken;
    if (broken !== undefined && pendingOutcomeRef.current) {
      const fn = pendingOutcomeRef.current;
      pendingOutcomeRef.current = null;
      fn(broken);
    }
  }, [broken]);

  /* Run animation once on mount */
  useEffect(() => {
    const stage  = stageRef.current;
    const caseEl = caseRef.current;
    const fx     = fxRef.current;
    const pick   = pickRef.current;
    const pOut   = pOutRef.current;
    const pMid   = pMidRef.current;
    const pHi    = pHiRef.current;
    const itemEl = itemRef.current;
    const flash  = flashRef.current;
    const floor  = floorRef.current;
    const $pw    = pwRef.current;
    const $pa    = paRef.current;
    const $pb    = pbRef.current;
    const $burst = burstRef.current;
    if (!stage) return;

    let cancelled = false;
    const token = { v: 0 };
    const my = ++token.v;
    const alive = () => !cancelled && token.v === my;

    /* helpers */
    const wait = ms => new Promise(r => setTimeout(r, ms));
    const anim = (el, f, o) => el.animate(f, Object.assign({ fill: 'forwards', easing: 'ease' }, o));
    function shake(k) {
      stage.classList.remove('cao-shakexs','cao-shakes','cao-shakem','cao-shakel','cao-shakexl');
      void stage.offsetWidth;
      stage.classList.add('cao-shake' + k);
    }
    function setFrame(i) {
      const c = i % COLS, r = Math.floor(i / COLS);
      caseEl.style.backgroundPosition = `${c / (COLS - 1) * 100}% ${r / (ROWS - 1) * 100}%`;
    }
    function px(frac) {
      const b = stage.getBoundingClientRect();
      return { x: frac[0] * b.width, y: frac[1] * b.height, w: b.width };
    }
    function placeAnchors() {
      const it = ANCHOR.item;
      fx.style.setProperty('--ax', it[0] * 100 + '%');
      fx.style.setProperty('--ay', it[1] * 100 + '%');
    }
    function drawPick(t) {
      const d = S_CLIP.map((p, i) => {
        const q = S_PICK[i];
        const k = t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        return (i ? 'L' : 'M') + (p[0] + (q[0] - p[0]) * k).toFixed(1) + ' ' + (p[1] + (q[1] - p[1]) * k).toFixed(1);
      }).join(' ');
      pOut.setAttribute('d', d); pMid.setAttribute('d', d); pHi.setAttribute('d', d);
    }
    function spark(x, y, n) {
      const c = RAR[rarity];
      for (let i = 0; i < n; i++) {
        const el = document.createElement('div');
        el.style.cssText = `position:absolute;border-radius:50%;width:4px;height:4px;background:#fff;box-shadow:0 0 9px 2px ${c.c2};left:${x}px;top:${y}px;pointer-events:none`;
        fx.appendChild(el);
        const a = Math.random() * Math.PI * 2, d = 14 + Math.random() * 30;
        el.animate([
          { transform: 'translate(-50%,-50%) scale(1)', opacity: 1 },
          { transform: `translate(calc(-50% + ${Math.cos(a) * d}px),calc(-50% + ${Math.sin(a) * d}px)) scale(0)`, opacity: 0 },
        ], { duration: 340 + Math.random() * 260, easing: 'cubic-bezier(.2,.8,.3,1)' }).onfinish = () => el.remove();
      }
    }
    function landingDust() {
      const p = px([0.5, 0.72]);
      for (let i = 0; i < 10; i++) {
        const el = document.createElement('div');
        el.style.cssText = `position:absolute;border-radius:50%;width:6px;height:3px;background:rgba(255,255,255,.3);box-shadow:0 0 8px 2px rgba(255,255,255,.15);left:${p.x}px;top:${p.y}px;pointer-events:none`;
        fx.appendChild(el);
        const dir = (Math.random() - 0.5) * 2;
        const dist = (30 + Math.random() * 60) * (p.w / 440);
        const up   = (10 + Math.random() * 25) * (p.w / 440);
        el.animate([
          { transform: 'translate(-50%,-50%) scale(.4)', opacity: .9 },
          { transform: `translate(calc(-50% + ${dir * dist}px),calc(-50% + ${-up}px)) scale(1)`, opacity: 0 },
        ], { duration: 420 + Math.random() * 200, easing: 'cubic-bezier(.2,.7,.3,1)' }).onfinish = () => el.remove();
      }
    }

    async function introDrop() {
      if (reduce) {
        caseEl.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 140, fill: 'forwards' });
        floor.animate([{ opacity: 0 }, { opacity: .45 }], { duration: 140, fill: 'forwards' });
        await wait(140);
        return;
      }
      const dur = 620;
      caseEl.animate(
        [{ opacity: 0, transform: 'translateY(-80%) rotate(-8deg)' },
         { opacity: 1, transform: 'translateY(4%) rotate(1.5deg)', offset: 0.6 },
         {              transform: 'translateY(-2%) rotate(-.5deg)', offset: 0.78 },
         { opacity: 1, transform: 'translateY(0) rotate(0)' }],
        { duration: dur, easing: 'cubic-bezier(.25,.55,.2,1)', fill: 'backwards' }
      );
      floor.animate([{ opacity: 0 }, { opacity: .45 }], { duration: dur, easing: 'ease-out', fill: 'forwards' });
      setTimeout(() => { if (alive()) { landingDust(); H.light(); shake('xs'); } }, Math.round(dur * 0.6));
      await wait(dur);
    }

    function burst(n, wave = 0) {
      const c = RAR[rarity];
      const core = wave === 0 ? c.c2 : wave === 1 ? c.c   : '#ffffff';
      const glow = wave === 0 ? c.c  : wave === 1 ? c.c2  : c.c2;
      const sz   = wave === 2 ? 3 : 5;
      const p = px(ANCHOR.item);
      for (let i = 0; i < n; i++) {
        const el = document.createElement('div');
        const d = (40 + Math.random() * 150) * (p.w / 440);
        const up = (30 + Math.random() * 110) * (p.w / 440);
        const a = Math.random() * Math.PI * 2;
        el.style.cssText = `position:absolute;border-radius:50%;width:${sz}px;height:${sz}px;background:${core};box-shadow:0 0 11px 3px ${glow};left:${p.x}px;top:${p.y}px;pointer-events:none`;
        fx.appendChild(el);
        el.animate([
          { transform: 'translate(-50%,-50%) scale(1)', opacity: 1 },
          { transform: `translate(calc(-50% + ${Math.cos(a) * d}px),calc(-50% + ${Math.sin(a) * d - up}px)) scale(0)`, opacity: 0 },
        ], { duration: 850 + Math.random() * 900, easing: 'cubic-bezier(.1,.7,.3,1)' }).onfinish = () => el.remove();
      }
    }
    function itemHTML() {
      const asset = itemAssetRef.current;
      const type  = itemTypeRef.current;
      const glow  = `drop-shadow(0 0 18px ${RAR[rarity].c})`;
      if (asset) {
        const radius = (type === 'banner') ? '14px' : '10px';
        const fit    = (type === 'banner') ? 'cover' : 'contain';
        return `<img src="${asset}" style="width:100%;height:auto;display:block;object-fit:${fit};border-radius:${radius};filter:${glow}"/>`;
      }
      if (type === 'skrep_bundle') return `<img src="/paperclip.svg" style="width:80%;margin:0 auto;display:block;filter:${glow} brightness(0) invert(1)"/>`;
      return '';
    }
    function playSeq(from, to, dur, onFrame, easing) {
      return new Promise(res => {
        const t0 = performance.now();
        (function step(now) {
          if (!alive()) return res();
          const t = Math.min(1, (now - t0) / dur);
          const te = easing ? easing(t) : t;
          const f = Math.round(from + (to - from) * te);
          setFrame(f); if (onFrame) onFrame(f);
          t < 1 ? requestAnimationFrame(step) : res();
        })(t0);
      });
    }
    function clearPieces() {
      [$pw, $pa, $pb, $burst].forEach(e => e?.getAnimations().forEach(a => a.cancel()));
      $pw.classList.remove('on');
    }

    async function snapPick(L, k) {
      const TENSE = 'translate(0px,0px) rotate(-28deg)';
      anim(pick, [{ transform: 'translate(0px,0px) rotate(-10deg)' }, { transform: TENSE }], { duration: reduce ? 60 : 260, easing: 'cubic-bezier(.5,0,.6,1)' });
      await wait(reduce ? 60 : 260); if (!alive()) return;
      for (let j = 0; j < (reduce ? 0 : 5); j++) {
        anim(pick, [{ transform: TENSE }, { transform: `translate(${(j % 2 ? 1 : -1) * 1.4 * k}px,${(j % 2 ? -1 : 1) * .8 * k}px) rotate(-29deg)` }, { transform: TENSE }], { duration: 55 });
        await wait(55); if (!alive()) return;
      }
      $pw.style.left = pick.style.left; $pw.style.top = pick.style.top;
      $pw.style.transformOrigin = pick.style.transformOrigin;
      $pw.style.transform = TENSE;
      pick.getAnimations().forEach(a => a.cancel()); pick.style.opacity = 0;
      $pw.classList.add('on');

      const ang = -28 * Math.PI / 180;
      const vx = (BRK_F[0] - TIP[0]) * $pw.offsetWidth, vy = (BRK_F[1] - TIP[1]) * $pw.offsetHeight;
      const bx = L.x + vx * Math.cos(ang) - vy * Math.sin(ang), by = L.y + vx * Math.sin(ang) + vy * Math.cos(ang);
      spark(bx, by, 12); shake('m');
      CasePatterns.breakSnap();
      if (!reduce) caseEl.animate(
        [{ transform: 'translateX(0)' },
         { transform: 'translateX(-4px)', offset: 0.2 },
         { transform: 'translateX(3px)',  offset: 0.5 },
         { transform: 'translateX(-1px)', offset: 0.75 },
         { transform: 'translateX(0)' }],
        { duration: 320, easing: 'cubic-bezier(.2,.8,.3,1)' }
      );
      setTimeout(() => { if (alive()) spark(bx, by, 8); }, 180);
      $burst.animate([{ opacity: 1, transform: 'scale(.5)' }, { opacity: 1, transform: 'scale(1.1)', offset: .35 }, { opacity: 0, transform: 'scale(1.35)' }],
        { duration: reduce ? 80 : 420, easing: 'ease-out', fill: 'forwards' });
      $pa.animate([{ transform: 'none', opacity: 1 },
        { transform: `translate(${-6 * k}px,${-4 * k}px) rotate(-14deg)`, opacity: 1, offset: .14 },
        { transform: `translate(${-26 * k}px,${60 * k}px) rotate(-48deg)`, opacity: 1, offset: .5 },
        { transform: `translate(${-46 * k}px,${250 * k}px) rotate(-130deg)`, opacity: 0 }],
        { duration: reduce ? 120 : 1050, easing: 'cubic-bezier(.4,0,.85,.5)', fill: 'forwards' });
      $pb.animate([{ transform: 'none', opacity: 1 },
        { transform: 'rotate(5deg)', offset: .12 }, { transform: 'rotate(-3deg)', offset: .24 },
        { transform: 'rotate(1.5deg)', offset: .34 }, { transform: 'rotate(0deg)', offset: .5 },
        { transform: `translate(${6 * k}px,${14 * k}px) rotate(22deg)`, offset: .64 },
        { transform: `translate(${14 * k}px,${170 * k}px) rotate(80deg)`, opacity: 0 }],
        { duration: reduce ? 120 : 1400, easing: 'ease-in', fill: 'forwards' });
      await wait(reduce ? 120 : 520); if (!alive()) return;
      shake('s'); CasePatterns.breakFinal();
      await wait(reduce ? 60 : 900); if (!alive()) return;
      /* broken done */
      pick.style.opacity = 0; $pw.classList.remove('on'); setFrame(0);
      stage.classList.remove('opened');
      onDone?.();
    }

    async function open() {
      // Пока webp-спрайт грузится — показываем skeleton-плейсхолдер
      caseEl.classList.add('cao-case-loading');
      await preload(rarity); if (!alive()) return;
      caseEl.classList.remove('cao-case-loading');

      const c = RAR[rarity];
      const T = TIMINGS[rarity];
      caseEl.style.backgroundImage = `url(${SHEETS[rarity]})`;
      document.documentElement.style.setProperty('--cao-r', c.c);
      document.documentElement.style.setProperty('--cao-r2', c.c2);
      clearPieces(); setFrame(0); stage.classList.remove('opened');
      pick.style.opacity = 0; drawPick(0);
      fx.querySelectorAll('.cao-spark,.cao-dust').forEach(n => n.remove());
      floor.style.opacity = '.45';
      itemEl.className = 'cao-item r-' + rarity;
      itemEl.innerHTML = '';
      if (seamRef.current)  seamRef.current.className  = 'cao-seam';
      if (sweepRef.current) sweepRef.current.className = 'cao-chroma-sweep';
      placeAnchors();

      CasePatterns.press();
      await introDrop(); if (!alive()) return;

      const L = px(ANCHOR.lock), b = stage.getBoundingClientRect(), k = b.width / 440;
      const pw = b.width * 0.34, ph = pw * VB.h / VB.w;
      pick.style.left = (L.x - TIP[0] * pw) + 'px';
      pick.style.top  = (L.y - TIP[1] * ph) + 'px';
      pick.style.opacity = 1;
      const HOVER = `translate(${-30 * k}px,${70 * k}px) rotate(-12deg)`;
      anim(pick, [{ transform: `translate(${-110 * k}px,${150 * k}px) rotate(-150deg) scale(.55)`, opacity: 0 }, { transform: HOVER, opacity: 1 }],
        { duration: reduce ? 120 : T.flyIn, easing: 'cubic-bezier(.2,1.1,.3,1)' });
      await wait(reduce ? 120 : T.flyIn + 40); if (!alive()) return;

      /* Morph clip→pick with secondary motion wobble on pick wrapper */
      if (!goldenRef.current && !reduce) CasePatterns.clipMorph();
      const morphDur = reduce ? 60 : T.morph;
      let morphAnim = null;
      if (!reduce) {
        const HOVER_A = `translate(${-30 * k}px,${70 * k}px) rotate(-15deg)`;
        const HOVER_B = `translate(${-30 * k}px,${70 * k}px) rotate(-9deg)`;
        morphAnim = pick.animate(
          [{ transform: HOVER }, { transform: HOVER_A, offset: 0.25 }, { transform: HOVER_B, offset: 0.55 }, { transform: HOVER_A, offset: 0.8 }, { transform: HOVER }],
          { duration: morphDur, easing: 'linear', composite: 'replace' }
        );
      }
      const t0 = performance.now();
      await new Promise(r => requestAnimationFrame(function s(n) {
        const t = Math.min(1, (n - t0) / morphDur); drawPick(t);
        (t < 1 && alive()) ? requestAnimationFrame(s) : r();
      }));
      if (morphAnim) try { morphAnim.cancel(); } catch {}
      if (!alive()) return;

      const AT = 'translate(0px,0px) rotate(-10deg)';
      anim(pick, [{ transform: HOVER }, { transform: AT }], { duration: reduce ? 100 : T.insert, easing: 'cubic-bezier(.3,.9,.2,1)' });
      await wait(reduce ? 100 : T.insert + 20); if (!alive()) return;

      CasePatterns.clipIn(rarity);

      if (goldenRef.current) {
        H.rigid();
        anim(pick, [{ transform: AT }, { transform: 'translate(0px,-1px) rotate(-11deg)' }, { transform: AT }],
          { duration: reduce ? 60 : 260, easing: 'ease-in-out' });
        await wait(reduce ? 60 : 260); if (!alive()) return;
      }

      const triesTotal = goldenRef.current ? 1 : c.tries;
      const fakeAt = (!goldenRef.current && triesTotal >= 3 && (rarity === 'legendary' || rarity === 'chroma')) ? triesTotal - 2 : -1;

      for (let i = 0; i < triesTotal; i++) {
        const last = i === triesTotal - 1;
        const isFake = i === fakeAt;
        const pinDur = reduce ? 80 : T.pin;
        const rotDeep = isFake ? -32 : -26 - i * 4;

        anim(pick, [{ transform: AT },
          { transform: `translate(${-3 * k}px,${1 * k}px) rotate(${rotDeep}deg)` },
          { transform: `translate(${1 * k}px,0px) rotate(${-4 + i}deg)` }, { transform: AT }],
          { duration: pinDur, easing: 'ease-in-out' });

        const shakeClass = last ? 'm' : (isFake ? 'l' : (i >= triesTotal - 2 ? 's' : 'xs'));
        shake(shakeClass);
        spark(L.x, L.y, last ? 10 : (isFake ? 8 : 4));

        if (!reduce && (rarity === 'legendary' || rarity === 'chroma') && !last) {
          caseEl.animate(
            [{ transform: 'translate(0,0)' },
             { transform: `translate(${-1 - k * 0.3}px,.5px)`, offset: 0.3 },
             { transform: `translate(${1 + k * 0.3}px,-.5px)`, offset: 0.6 },
             { transform: 'translate(0,0)' }],
            { duration: 220, easing: 'ease-in-out' }
          );
        }

        if (isFake && !reduce && seamRef.current) {
          seamRef.current.classList.add('on');
          const seamNode = seamRef.current;
          setTimeout(() => { if (alive() && seamNode) seamNode.classList.remove('on'); }, 220);
        }

        if (!goldenRef.current) CasePatterns.pin(rarity, i, triesTotal);
        await wait(pinDur + (isFake ? 180 : 0)); if (!alive()) return;
      }

      /* Wait for broken prop if not yet known; value passed via resolve to avoid stale closure */
      let resolvedBroken = brokenRef.current;
      if (resolvedBroken === undefined) {
        if (goldenRef.current) {
          resolvedBroken = false; // golden always wins — no need to wait for API
        } else {
          resolvedBroken = await new Promise(r => {
            pendingOutcomeRef.current = r;
            // broken may have arrived between the tries loop and now
            if (brokenRef.current !== undefined) {
              pendingOutcomeRef.current = null;
              r(brokenRef.current);
            }
          });
          if (!alive()) return;
        }
      }

      const isBroken = resolvedBroken === true;
      if (isBroken) { await snapPick(L, k); return; }

      spark(L.x, L.y, 20);
      const yieldShake = rarity === 'common' ? 'm' : rarity === 'epic' ? 'l' : 'xl';
      shake(yieldShake);
      CasePatterns.yield_(rarity);
      if (seamRef.current && !reduce) seamRef.current.classList.add('on');
      anim(pick, [{ transform: AT, opacity: 1 }, { transform: `translate(${70 * k}px,${120 * k}px) rotate(75deg)`, opacity: 0 }],
        { duration: reduce ? 100 : 420, easing: 'ease-in' });
      await wait(reduce ? 80 : T.yieldPause); if (!alive()) return;

      floor.style.opacity = '.9';
      if (!reduce) floor.animate([{ opacity: '.9' }, { opacity: '1' }, { opacity: '.9' }], { duration: T.yieldPause + 100 });

      const lidEase = t => {
        if (t < 0.65) { const u = t / 0.65; return u * u * (3 - 2 * u) * 0.86; }
        const u = (t - 0.65) / 0.35;
        return 0.86 + (1 - Math.pow(1 - u, 2)) * 0.14 + Math.sin(u * Math.PI) * 0.025;
      };

      let popped = false;
      await playSeq(0, FRAMES - 1, reduce ? 150 : T.sprite, f => {
        if (!popped && f >= 18) {
          popped = true;
          flash.classList.remove('cao-pop'); void flash.offsetWidth; flash.classList.add('cao-pop');
          stage.classList.add('opened');
          shake(rarity === 'common' ? 'm' : rarity === 'epic' ? 'l' : 'xl');
          CasePatterns.drop(rarity);
          if (rarity === 'chroma' && sweepRef.current && !reduce) sweepRef.current.classList.add('on');
          if (seamRef.current) seamRef.current.classList.add('off');
          if (!reduce) caseEl.animate(
            [{ transform: 'scale(1)' },
             { transform: 'scale(1.028)', offset: 0.4 },
             { transform: 'scale(.996)', offset: 0.72 },
             { transform: 'scale(1)' }],
            { duration: 280, easing: 'cubic-bezier(.2,1.3,.3,1)' }
          );
          const n1 = rarity === 'common' ? 14 : rarity === 'epic' ? 24 : rarity === 'legendary' ? 38 : 52;
          burst(n1, 0);
          if (!reduce) {
            const n2 = rarity === 'common' ? 8 : rarity === 'epic' ? 14 : rarity === 'legendary' ? 22 : 32;
            setTimeout(() => { if (alive()) burst(n2, 1); }, 180);
            if (rarity === 'chroma') setTimeout(() => { if (alive()) burst(14, 2); }, 360);
          }
          itemEl.innerHTML = itemHTML();
        }
      }, lidEase);
      if (!alive()) return;
      onDone?.();
    }

    /* click to skip — only allowed once API result is known (broken ref set) */
    const skipHandler = () => {
      if (cancelled) return;
      if (brokenRef.current === undefined) return;
      cancelled = true;
      if (brokenRef.current === true) CasePatterns.breakSnap();
      else                            CasePatterns.skip(rarity);
      setFrame(FRAMES - 1);
      pick.style.opacity = 0;
      itemEl.innerHTML = itemHTML();
      stage.classList.add('opened');
      floor.style.opacity = '.9';
      onDone?.();
    };
    stage.addEventListener('click', skipHandler);

    open();
    return () => {
      cancelled = true;
      cancelHaptics();
      stage.removeEventListener('click', skipHandler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const burstLines = [[-5,-13,-13,-29],[5,-13,13,-29],[-5,13,-13,29],[5,13,13,29]]
    .map(([a,b,c,d]) => `<line x1="${BRK[0]+a}" y1="${BRK[1]+b}" x2="${BRK[0]+c}" y2="${BRK[1]+d}"/>`)
    .join('');

  return (
    <>
      <style>{`
        :root{--cao-r:#6f8dff;--cao-r2:#b9c8ff}
        .cao-stage{position:relative;width:min(92vw,470px);aspect-ratio:1/1;margin:0 auto}
        .cao-glowfloor{position:absolute;left:50%;top:70%;width:78%;height:34%;translate:-50% -50%;
          background:radial-gradient(50% 50% at 50% 50%,color-mix(in srgb,var(--cao-r) 55%,transparent),transparent 72%);
          filter:blur(16px);opacity:.45;transition:opacity .5s;will-change:opacity}
        .cao-shadow{position:absolute;left:46%;top:72%;width:52%;height:13%;translate:-50% -50%;
          background:radial-gradient(50% 50% at 50% 50%,rgba(0,0,0,.85),transparent 72%);filter:blur(9px)}
        .cao-case{position:absolute;inset:0;background-repeat:no-repeat;background-size:600% 500%;background-position:0% 0%;will-change:transform}
        /* skeleton-пульс только пока webp-спрайт грузится */
        .cao-case.cao-case-loading::before{content:"";position:absolute;left:20%;right:20%;top:30%;bottom:25%;border-radius:14px;
          background:radial-gradient(ellipse at 50% 50%,rgba(255,255,255,.06),transparent 72%);
          animation:cao-skeleton 1.4s ease-in-out infinite;pointer-events:none}
        @keyframes cao-skeleton{0%,100%{opacity:.3}50%{opacity:.75}}
        .cao-fx{position:absolute;inset:0;pointer-events:none;overflow:visible}
        .cao-beam{position:absolute;left:var(--ax);top:var(--ay);width:34%;height:54%;translate:-50% -100%;
          clip-path:polygon(40% 100%,60% 100%,100% 0,0 0);
          background:linear-gradient(0deg,color-mix(in srgb,var(--cao-r) 90%,transparent),transparent 76%);
          filter:blur(8px);mix-blend-mode:screen;opacity:0;transform:scaleY(.25);transform-origin:bottom;
          transition:opacity .45s,transform .7s cubic-bezier(.2,1,.3,1)}
        .cao-rays{position:absolute;left:var(--ax);top:var(--ay);width:70%;height:70%;translate:-50% -55%;opacity:0;
          background:conic-gradient(from 0deg,transparent 0 5deg,color-mix(in srgb,var(--cao-r2) 65%,transparent) 5deg 7deg,transparent 7deg 22deg);
          mask:radial-gradient(closest-side,transparent 16%,#000 30%,transparent 70%);
          -webkit-mask:radial-gradient(closest-side,transparent 16%,#000 30%,transparent 70%);
          mix-blend-mode:screen;animation:cao-spin 16s linear infinite;transition:opacity .6s}
        @keyframes cao-spin{to{rotate:360deg}}
        .cao-chroma-sweep{position:absolute;left:var(--ax);top:var(--ay);width:92%;height:92%;translate:-50% -50%;
          background:conic-gradient(from 0deg,
            #35e6bf 0%,#7afce0 15%,#c2fff2 28%,#ff9ddd 44%,#a480ff 60%,#35b6ff 76%,#35e6bf 100%);
          mask:radial-gradient(closest-side,transparent 24%,#000 44%,transparent 82%);
          -webkit-mask:radial-gradient(closest-side,transparent 24%,#000 44%,transparent 82%);
          mix-blend-mode:screen;opacity:0;filter:blur(2px);pointer-events:none;
          animation:cao-sweep 4s linear infinite;transition:opacity .5s ease-out;will-change:transform,opacity}
        .cao-chroma-sweep.on{opacity:.55}
        @keyframes cao-sweep{to{rotate:360deg}}
        .cao-seam{position:absolute;left:16%;right:16%;top:40%;height:3px;border-radius:3px;
          background:linear-gradient(90deg,transparent 0%,var(--cao-r2) 50%,transparent 100%);
          opacity:0;transform:scaleX(0);transform-origin:center;filter:blur(1px);
          box-shadow:0 0 12px 2px var(--cao-r);mix-blend-mode:screen;pointer-events:none;will-change:transform,opacity}
        .cao-seam.on{opacity:.95;transform:scaleX(1);transition:opacity .18s ease-out,transform .22s cubic-bezier(.2,1.2,.3,1)}
        .cao-seam.off{opacity:0;transition:opacity .25s ease-out}
        .cao-item{position:absolute;left:var(--ax);top:var(--ay);width:42%;translate:-50% -50%;
          opacity:0;transform:translateY(26px) scale(.45);
          transition:opacity .45s,transform .8s cubic-bezier(.2,1.3,.35,1);will-change:transform,opacity,filter}
        .cao-stage.opened .cao-beam{opacity:.85;transform:scaleY(1)}
        .cao-stage.opened .cao-rays{opacity:.45}
        .cao-stage.opened .cao-item{opacity:1;transform:translateY(-34%) scale(1)}
        .cao-item.r-common,.cao-item.r-epic,.cao-item.r-legendary,.cao-item.r-chroma{transition:none}
        .cao-stage.opened .cao-item.r-common{animation:cao-in-common .5s cubic-bezier(.2,1.2,.3,1) forwards,cao-float 3.6s ease-in-out .7s infinite}
        .cao-stage.opened .cao-item.r-epic{animation:cao-in-epic .62s cubic-bezier(.2,1.3,.3,1) forwards,cao-float 3.4s ease-in-out .8s infinite}
        .cao-stage.opened .cao-item.r-legendary{animation:cao-in-leg .84s cubic-bezier(.2,1.4,.3,1) forwards,cao-float 3.6s ease-in-out 1.05s infinite}
        .cao-stage.opened .cao-item.r-chroma{animation:cao-in-chr 1.15s cubic-bezier(.2,1.5,.3,1) forwards,cao-float 3.8s ease-in-out 1.3s infinite,cao-chr-hue 6s linear infinite}
        @keyframes cao-in-common{from{opacity:0;transform:translateY(10%) scale(.5)}to{opacity:1;transform:translateY(-34%) scale(1)}}
        @keyframes cao-in-epic{0%{opacity:0;transform:translateY(14%) scale(.4) rotate(-3deg)}70%{opacity:1;transform:translateY(-36%) scale(1.04) rotate(1.5deg)}100%{opacity:1;transform:translateY(-34%) scale(1) rotate(0)}}
        @keyframes cao-in-leg{0%{opacity:0;transform:translateY(18%) scale(.35)}55%{opacity:1;transform:translateY(-38%) scale(1.1)}80%{transform:translateY(-32%) scale(.98)}100%{opacity:1;transform:translateY(-34%) scale(1)}}
        @keyframes cao-in-chr{0%{opacity:0;transform:translateY(20%) scale(.3) rotate(-8deg)}40%{opacity:1;transform:translateY(-40%) scale(1.14) rotate(5deg)}70%{transform:translateY(-30%) scale(.95) rotate(-2deg)}100%{opacity:1;transform:translateY(-34%) scale(1) rotate(0)}}
        @keyframes cao-chr-hue{0%{filter:hue-rotate(0deg)}100%{filter:hue-rotate(360deg)}}
        @keyframes cao-float{0%,100%{margin-top:0}50%{margin-top:-10px}}
        .cao-flash{position:absolute;left:var(--ax);top:var(--ay);width:60%;height:60%;translate:-50% -50%;
          background:radial-gradient(50% 50% at 50% 50%,#fff,transparent 66%);opacity:0;mix-blend-mode:screen}
        .cao-flash.cao-pop{animation:cao-pop .5s ease-out}
        @keyframes cao-pop{0%{opacity:0;scale:.4}18%{opacity:.95}100%{opacity:0;scale:1.5}}
        .cao-pick{position:absolute;width:34%;aspect-ratio:490/110;opacity:0;will-change:transform,opacity}
        .cao-pick svg{width:100%;height:100%;overflow:visible;filter:drop-shadow(0 2px 3px rgba(0,0,0,.75))}
        .cao-pick path{fill:none;stroke-linecap:round;stroke-linejoin:round}
        .cao-pieceWrap{position:absolute;width:34%;aspect-ratio:490/110;display:none;pointer-events:none}
        .cao-pieceWrap.on{display:block}
        .cao-piece,.cao-burst{position:absolute;inset:0;width:100%;height:100%;overflow:visible}
        .cao-piece svg{width:100%;height:100%;overflow:visible;filter:drop-shadow(0 2px 3px rgba(0,0,0,.75))}
        .cao-burst line{stroke:#a3aab2;stroke-width:7;stroke-linecap:round}
        .cao-shakexs{animation:cao-shk-xs .18s linear}
        .cao-shakes{animation:cao-shk .2s linear}
        .cao-shakem{animation:cao-shk .32s linear}
        .cao-shakel{animation:cao-shk .5s linear}
        .cao-shakexl{animation:cao-shk-xl .55s linear}
        @keyframes cao-shk{0%,100%{translate:0 0}15%{translate:-4px 2px}35%{translate:5px -3px}55%{translate:-3px -2px}75%{translate:3px 3px}}
        @keyframes cao-shk-xs{0%,100%{translate:0 0}25%{translate:-2px 1px}50%{translate:2px -1px}75%{translate:-1px 1px}}
        @keyframes cao-shk-xl{0%,100%{translate:0 0}12%{translate:-7px 3px}28%{translate:8px -4px}44%{translate:-6px -4px}60%{translate:6px 5px}78%{translate:-3px 2px}}
        @media (prefers-reduced-motion:reduce){
          .cao-seam,.cao-chroma-sweep{display:none}
          .cao-stage.opened .cao-item.r-common,
          .cao-stage.opened .cao-item.r-epic,
          .cao-stage.opened .cao-item.r-legendary,
          .cao-stage.opened .cao-item.r-chroma{animation:none;opacity:1;transform:translateY(-34%) scale(1)}
        }
      `}</style>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%' }}>
        <div ref={stageRef} className="cao-stage">
          <div ref={floorRef} className="cao-glowfloor" />
          <div className="cao-shadow" />
          <div ref={caseRef} className="cao-case" />
          <div ref={seamRef} className="cao-seam" />
          <div ref={fxRef} className="cao-fx">
            <div className="cao-rays" />
            {rarity === 'chroma' && <div ref={sweepRef} className="cao-chroma-sweep" />}
            <div className="cao-beam" />
            <div ref={itemRef} className="cao-item" />
            <div ref={flashRef} className="cao-flash" />
            <div ref={pwRef} className="cao-pieceWrap" style={{ transformOrigin: `${BRK_F[0]*100}% ${BRK_F[1]*100}%` }}>
              <div ref={paRef} className="cao-piece"
                style={{ transformOrigin: `${BRK_F[0]*100}% ${BRK_F[1]*100}%` }}
                dangerouslySetInnerHTML={{ __html: pieceSVG(S_PICK.slice(0, BRK_I + 1), 'start', golden) }}/>
              <div ref={pbRef} className="cao-piece"
                style={{ transformOrigin: `${TIP[0]*100}% ${TIP[1]*100}%` }}
                dangerouslySetInnerHTML={{ __html: pieceSVG(S_PICK.slice(BRK_I), 'end', golden) }}/>
              <svg ref={burstRef} className="cao-burst" preserveAspectRatio="xMidYMid meet"
                viewBox={`${VB.x} ${VB.y} ${VB.w} ${VB.h}`}
                style={{ transformOrigin: `${BRK_F[0]*100}% ${BRK_F[1]*100}%` }}
                dangerouslySetInnerHTML={{ __html: burstLines }}/>
            </div>
            <div ref={pickRef} className="cao-pick"
              style={{ transformOrigin: `${TIP[0]*100}% ${TIP[1]*100}%` }}>
              <svg viewBox="15 45 490 110" preserveAspectRatio="xMidYMid meet">
                <defs>
                  <linearGradient id="clipMetal" x1="30" y1="50" x2="290" y2="150" gradientUnits="userSpaceOnUse" spreadMethod="reflect">
                    {golden ? <>
                      <stop offset="0" stopColor="#fff8c0"/><stop offset="0.2" stopColor="#ffd966"/>
                      <stop offset="0.4" stopColor="#c8860a"/><stop offset="0.55" stopColor="#ffe880"/>
                      <stop offset="0.75" stopColor="#d4a017"/><stop offset="1" stopColor="#8b6000"/>
                    </> : <>
                      <stop offset="0" stopColor="#ffffff"/><stop offset="0.2" stopColor="#d9dde2"/>
                      <stop offset="0.4" stopColor="#8a9199"/><stop offset="0.55" stopColor="#f4f6f8"/>
                      <stop offset="0.75" stopColor="#a3aab2"/><stop offset="1" stopColor="#6b727a"/>
                    </>}
                  </linearGradient>
                </defs>
                <path ref={pOutRef} stroke={golden ? '#7a5200' : '#5f666e'} strokeWidth="20" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                <path ref={pMidRef} stroke="url(#clipMetal)" strokeWidth="14" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                <path ref={pHiRef} stroke="#ffffff" strokeWidth="3" opacity=".7" fill="none" strokeLinecap="round" strokeLinejoin="round" transform="translate(-2 -2.5)"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
